/* DealRoomReturnRibbon.jsx — Calm "Since Your Last Visit" digest.
 *
 * Premium contextual ribbon rendered at the top of the Deal Room. Only
 * surfaces when there ARE new updates — otherwise it stays invisible so
 * the page is never noisy.
 *
 * Visual identity: violet-soft glass card matching the rest of the Deal
 * Room. NO aggressive red, NO flashing, NO badge spam. One calm
 * sentence + a compact group strip + one action: "Jump to first new".
 *
 * Persistence: reads `/api/v2/deals/{id}/since-last-visit` on mount and
 * stamps `/seen` when the user dismisses or jumps. Dismiss-on-jump means
 * the ribbon never re-appears for the same digest.
 */

import React, { useEffect, useMemo, useState } from "react";
import {
  AlertOctagon,
  ArrowRight,
  Calendar,
  ClipboardCheck,
  Clock,
  FileText,
  GitBranch,
  Info,
  Mail,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import { api } from "../../lib/api";

const ICONS = {
  file:      FileText,
  milestone: GitBranch,
  users:     Users,
  mortgage:  Sparkles,
  chain:     GitBranch,
  alert:     AlertOctagon,
  calendar:  Calendar,
  clock:     Clock,
  task:      ClipboardCheck,
  message:   Mail,
  info:      Info,
};

const fmtAgo = (iso) => {
  if (!iso) return "first visit";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000) return "just now";
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  const days = Math.floor(ms / 86_400_000);
  return days < 2 ? "yesterday" : `${days}d ago`;
};

export const DealRoomReturnRibbon = ({ dealId, onJumpToHistory }) => {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!dealId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(
          `/api/v2/deals/${dealId}/since-last-visit`,
        );
        if (!cancelled) setData(data);
      } catch (_) {
        // Silent — visit tracking is enrichment, not authoritative.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  const stampSeen = async () => {
    try {
      await api.post(`/api/v2/deals/${dealId}/seen`, {});
    } catch (_) {
      // Silent
    }
  };

  const handleJump = async () => {
    if (onJumpToHistory) onJumpToHistory(data?.first_unread_activity_id);
    setDismissed(true);
    await stampSeen();
  };

  const handleDismiss = async () => {
    setDismissed(true);
    await stampSeen();
  };

  /* Render rules:
     • Never render the ribbon on first-visit-no-updates (`Welcome.`)
     • Never render on returning-visit with 0 updates
     • Never re-render after dismiss
     • Show ONLY when there are real new updates */
  const shouldRender = useMemo(() => {
    if (!data || dismissed) return false;
    return (data.total || 0) > 0;
  }, [data, dismissed]);

  if (!shouldRender) return null;

  const groups = (data.groups || []).slice(0, 5);

  return (
    <aside
      className="rounded-2xl p-4 sm:p-5 cf-fade-up"
      style={{
        background: "linear-gradient(180deg, rgba(124,58,237,0.04) 0%, rgba(255,255,255,0.6) 100%)",
        border: "1px solid var(--gp-violet-border)",
        boxShadow: "0 4px 18px -6px rgba(124,58,237,0.18)",
      }}
      data-testid="deal-return-ribbon"
    >
      <div className="flex items-start gap-3.5">
        <div
          className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg, #6D28D9 0%, #4F46E5 100%)",
            color: "#FFFFFF",
            boxShadow: "0 4px 10px -4px rgba(99,102,241,0.5)",
          }}
        >
          <Clock size={15} />
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="gp-eyebrow mb-1 flex items-center gap-2"
            style={{ color: "var(--gp-violet-deep)" }}
          >
            SINCE YOUR LAST VISIT · {fmtAgo(data.last_seen_at)}
          </div>
          <p
            className="text-[14px] sm:text-[15px] font-semibold leading-snug"
            style={{ color: "var(--gp-text)" }}
            data-testid="deal-return-headline"
          >
            {data.headline}
          </p>
          {/* Compact group strip — only show first 5, never spam */}
          {groups.length > 0 && (
            <div
              className="mt-3 flex flex-wrap items-center gap-1.5"
              data-testid="deal-return-groups"
            >
              {groups.map((g) => {
                const Icon = ICONS[g.icon_key] || Info;
                return (
                  <span
                    key={g.category}
                    className="inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold"
                    style={{
                      background: "#FFFFFF",
                      color: "var(--gp-text)",
                      border: "1px solid var(--gp-card-border)",
                    }}
                    data-testid={`deal-return-group-${g.category}`}
                    title={g.sample_titles.join(" · ")}
                  >
                    <Icon size={11} style={{ color: "var(--gp-violet-deep)" }} />
                    {g.count} {g.label.toLowerCase()}
                  </span>
                );
              })}
            </div>
          )}
          {/* Inline actions */}
          <div className="mt-3.5 flex items-center gap-2">
            <button
              type="button"
              onClick={handleJump}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-[12px] font-bold text-white gp-btn-primary"
              data-testid="deal-return-jump"
            >
              View updates <ArrowRight size={11} />
            </button>
            <button
              type="button"
              onClick={handleDismiss}
              className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-[11.5px] font-semibold transition-colors hover:bg-slate-100"
              style={{ color: "var(--gp-text-muted)" }}
              data-testid="deal-return-mark-seen"
            >
              Mark as seen
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-slate-100"
          style={{ color: "var(--gp-text-subtle)" }}
          data-testid="deal-return-close"
        >
          <X size={13} />
        </button>
      </div>
    </aside>
  );
};
